"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Link as LinkIcon, Users, Settings } from "lucide-react";
import InviteForm from "./InviteForm";
import CollaboratorList from "./CollaboratorList";

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  isOwner: boolean;
  shareLinkPath?: string;
  resourceLabel?: string;
}

type Tab = "invite" | "link" | "manage";

export default function ShareDialog({
  isOpen,
  onClose,
  sessionId,
  isOwner,
  shareLinkPath = `/shared/${sessionId}`,
  resourceLabel = "session",
}: ShareDialogProps) {
  const [activeTab, setActiveTab] = useState<Tab>("invite");
  const [shareLink, setShareLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkError, setLinkError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const generateShareLink = async () => {
    setIsGenerating(true);
    setLinkError("");
    try {
      // Set session as public in database
      const res = await fetch(`/api/sessions/${sessionId}/sharing`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic: true, allowCollaboration: true }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to enable sharing");
      }
      // Generate the share link
      const link = `${window.location.origin}${shareLinkPath.startsWith("/") ? shareLinkPath : `/${shareLinkPath}`}`;
      setShareLink(link);
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : "Failed to generate link");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    await navigator.clipboard.writeText(shareLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const tabs = [
    { id: "invite" as Tab, label: "Invite People", icon: Users },
    { id: "link" as Tab, label: "Share Link", icon: LinkIcon },
    { id: "manage" as Tab, label: "Manage Access", icon: Settings },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-50"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-think-sm border-2 border-black shadow-2xl z-50 overflow-hidden"
          >
            {/* Header */}
            <div className="border-b-2 border-black px-6 py-4 flex items-center justify-between bg-purple text-white">
              <div>
                <h2 className="text-2xl font-black uppercase">
                  Share {resourceLabel}
                </h2>
                <p className="text-sm opacity-90 mt-1">
                  Collaborate with others on this {resourceLabel}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-full transition"
                aria-label="Close"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="border-b-2 border-black bg-gray-50 flex">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 px-6 py-4 flex items-center justify-center gap-2 font-bold uppercase text-sm transition border-r-2 border-black last:border-r-0 ${
                      activeTab === tab.id
                        ? "bg-white text-purple"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            {/* Content */}
            <div className="p-6 max-h-[500px] overflow-y-auto">
              {activeTab === "invite" && (
                <div className="stack gap-4">
                  <div className="stack gap-2">
                    <h3 className="font-black uppercase text-lg">
                      Invite Collaborators
                    </h3>
                    <p className="text-sm text-gray-600">
                      Send an invitation to collaborate on this {resourceLabel}. You can
                      grant view-only or edit access.
                    </p>
                  </div>
                  <InviteForm sessionId={sessionId} resourcePath={shareLinkPath} />
                </div>
              )}

              {activeTab === "link" && (
                <div className="stack gap-4">
                  <div className="stack gap-2">
                    <h3 className="font-black uppercase text-lg">
                      Public Share Link
                    </h3>
                    <p className="text-sm text-gray-600">
                      Anyone with this link can view this {resourceLabel} (read-only).
                      To grant edit access, use the Invite tab instead.
                    </p>
                  </div>

                  {linkError && (
                    <p className="text-sm text-red-600 border-2 border-red-200 bg-red-50 px-4 py-2">
                      {linkError}
                    </p>
                  )}

                  {!shareLink ? (
                    <button
                      type="button"
                      onClick={generateShareLink}
                      disabled={isGenerating}
                      className="w-full border-2 border-black bg-pink text-white font-black uppercase px-6 py-4 hover:bg-purple transition disabled:opacity-50"
                    >
                      {isGenerating ? "Enabling..." : "Generate Public Link"}
                    </button>
                  ) : (
                    <div className="stack gap-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={shareLink}
                          readOnly
                          className="flex-1 border-2 border-black px-4 py-3 font-mono text-sm"
                        />
                        <button
                          type="button"
                          onClick={copyToClipboard}
                          className="border-2 border-black bg-yellow px-6 py-3 font-black uppercase hover:bg-pink hover:text-white transition"
                        >
                          {linkCopied ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500">
                        This link allows anyone to view this {resourceLabel}. They won't
                        be able to make changes unless you explicitly invite them
                        with edit access.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "manage" && (
                <div className="stack gap-4">
                  <div className="stack gap-2">
                    <h3 className="font-black uppercase text-lg">
                      Manage Collaborators
                    </h3>
                    <p className="text-sm text-gray-600">
                      View and manage who has access to this {resourceLabel}.
                    </p>
                  </div>
                  <CollaboratorList sessionId={sessionId} isOwner={isOwner} />
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t-2 border-black px-6 py-4 bg-gray-50 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="border-2 border-black bg-white px-6 py-2 font-black uppercase hover:bg-gray-100 transition"
              >
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
